import { useMemo } from "react";
import { LayoutDashboard, Inbox, Settings, Users, BarChart3, LogOut, LayoutGrid } from "lucide-react";
import { ROLE_LABELS, useAuth } from "./auth";
import ThemeToggle from "./theme-toggle";
import LangToggle from "./lang-toggle";
import { useAppNav } from "./app-nav";
import site from "./site";
import { t } from "@/lib/i18n";

const brand = (site as { name?: string }).name || "Hub";

const TABS = [
  { type: "dashboard", label: t("Дашборд"), icon: LayoutDashboard },
  { type: "inboxapp", label: t("Входящие"), icon: Inbox },
  { type: "contactsapp", label: t("Контакты"), icon: Users },
  { type: "reports", label: t("Отчёты"), icon: BarChart3 },
  { type: "settings", label: t("Настройки"), icon: Settings },
];

/** Шапка рабочего пространства: переходы по разделам, возврат к хабу и выход. */
export default function AppBar() {
  const { user, signOut, stopViewAs } = useAuth();
  const { open, home, current } = useAppNav();

  const name = useMemo(() => user?.name || user?.email.split("@")[0] || t("Пользователь"), [user]);

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur">
      {user?.viewingAs ? (
        <div className="flex items-center justify-center gap-3 bg-amber-400/15 px-4 py-1.5 text-[11.5px] text-amber-800" data-testid="view-as-banner">
          <i className="ri-eye-line text-[13px] leading-none" aria-hidden />
          <span>
            {t("Вы смотрите Hub глазами сотрудника {name} ({role}): разделы, клиенты и переписка — как видит он", { name, role: ROLE_LABELS[user.role] })}
          </span>
          <button
            type="button"
            onClick={() => void stopViewAs()}
            className="inline-flex h-6 cursor-pointer items-center gap-1 whitespace-nowrap rounded-btn border border-amber-600/40 px-2 text-[11px] font-semibold text-amber-900 transition hover:bg-amber-400/25"
            data-testid="view-as-stop"
          >
            {t("Вернуться к себе ({name})", { name: user.viewingAs.byName })}
          </button>
        </div>
      ) : null}
      <div className="mx-auto flex h-16 w-full max-w-[1440px] items-center gap-3 px-4 md:px-6">
        <button type="button" onClick={home} className="flex cursor-pointer items-center gap-2.5" aria-label={t("Все разделы")}>
          <span className="grid size-9 flex-none place-items-center rounded-btn bg-primary text-base font-bold text-primary-foreground" aria-hidden>
            {brand.slice(0, 1).toUpperCase()}
          </span>
          <span className="flex min-w-0 flex-col items-start leading-tight">
            <span className="truncate text-sm font-bold">{brand}</span>
            <span className="truncate text-[11px] text-muted-foreground">{t("Рабочее пространство")}</span>
          </span>
        </button>

        <nav className="ms-2 hidden items-center gap-1 lg:flex">
          {TABS.map((tab) => {
            const active = current === tab.type;
            return (
              <button
                key={tab.type}
                type="button"
                onClick={() => open(tab.type)}
                className={`inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-btn px-3 py-2 text-sm font-medium transition ${
                  active ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                <tab.icon className="size-4 flex-none" aria-hidden />
                {tab.label}
              </button>
            );
          })}
        </nav>

        <div className="ms-auto flex items-center gap-2">
          <button
            type="button"
            onClick={home}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn border border-border px-2.5 py-2 text-xs font-semibold text-muted-foreground transition hover:border-primary/60 hover:text-primary"
            title={t("Все разделы на одном экране")}
          >
            <LayoutGrid className="size-3.5 flex-none" aria-hidden />
            <span className="hidden sm:inline">{t("Все разделы")}</span>
          </button>
          <span className="hidden max-w-[180px] flex-col items-end leading-tight md:flex" title={user?.email}>
            <span className="max-w-full truncate text-xs text-foreground">{name}</span>
            {user ? <span className="max-w-full truncate text-[10.5px] text-muted-foreground">{ROLE_LABELS[user.role]}</span> : null}
          </span>
          <LangToggle />
          <ThemeToggle />
          <button
            type="button"
            onClick={() => void signOut()}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn border border-border px-3 py-2 text-xs font-semibold text-muted-foreground transition hover:border-primary/60 hover:text-primary"
          >
            <LogOut className="size-3.5 flex-none" aria-hidden />
            {t("Выйти")}
          </button>
        </div>
      </div>
    </header>
  );
}